import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCalendarAlt, FaClock, FaMapMarkerAlt, FaCreditCard, FaCheck,
  FaSpinner, FaLock, FaFileInvoice, FaUsers, FaInfoCircle,
} from 'react-icons/fa';
import { processPayment, loadRazorpayScript, getPaymentStatusStyle } from '../../services/paymentService';
import { getApiUrl } from '../../config/api';
import './MeetingPaymentPage.css';

const MeetingPaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId: paramBookingId } = useParams();

  // Accept booking data either from router state (preferred) or URL param
  const {
    booking: initialBooking,
    room: initialRoom,
    bookingId: stateBookingId,
  } = location.state || {};

  const bookingId = stateBookingId || paramBookingId;

  const [booking, setBooking] = useState(initialBooking || null);
  const [room, setRoom] = useState(initialRoom || null);
  const [loading, setLoading] = useState(!initialBooking);
  const [paying, setPaying] = useState(false);
  const [paymentType, setPaymentType] = useState('full'); // 'full' | 'partial'
  const [razorpayReady, setRazorpayReady] = useState(false);

  useEffect(() => {
    loadRazorpayScript().then(setRazorpayReady);
  }, []);

  // Fetch booking if not passed via state
  useEffect(() => {
    if (initialBooking || !bookingId) return;
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    fetch(getApiUrl(`/api/meetings/bookings/${bookingId}`), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setBooking(data.booking);
          setRoom(data.booking?.room || null);
        } else {
          toast.error(data.message || 'Booking not found');
          navigate('/meetings/my-bookings');
        }
      })
      .catch(() => {
        toast.error('Failed to load booking');
        navigate('/meetings/my-bookings');
      })
      .finally(() => setLoading(false));
  }, [bookingId, initialBooking, navigate]);

  if (loading) {
    return (
      <div className="mpp-loading">
        <FaSpinner className="mpp-spinner" />
        <p>Loading booking details...</p>
      </div>
    );
  }

  if (!booking) return null;

  const r = room || booking?.room || {};
  const pricePerHour = r.pricePerHour || 0;
  const cleaningCharge = r.cleaningCharge || 0;
  const startTime = new Date(booking.startTime);
  const endTime = new Date(booking.endTime);
  const durationHours = (endTime - startTime) / (1000 * 60 * 60);
  const baseAmount = Math.round(durationHours * pricePerHour);
  const fullAmount = baseAmount + cleaningCharge;
  const partialAmount = Math.round(fullAmount * 0.5);
  const payAmount = paymentType === 'partial' ? partialAmount : fullAmount;

  const formatTime = (date) =>
    date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  const formatDate = (date) =>
    date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  const handlePay = async () => {
    if (!razorpayReady) {
      toast.error('Payment gateway still loading. Please wait.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const currentUser = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    })();

    setPaying(true);
    try {
      const { verifyResult } = await processPayment({
        bookingType: 'meeting',
        token,
        orderPayload: { bookingId: booking._id, paymentType },
        checkoutMeta: {
          name: 'UBSAAPP – Meeting Room',
          description: `${r.name || 'Meeting Room'} · ${formatDate(startTime)}`,
          prefill: {
            name: currentUser.name || '',
            email: currentUser.email || '',
            contact: currentUser.phoneNumber || '',
          },
        },
      });

      toast.success('Payment successful! Your meeting room is confirmed.');
      navigate('/meetings/my-bookings', {
        state: { paymentSuccess: true, bookingId: booking._id, paymentId: verifyResult.payment?._id },
      });
    } catch (err) {
      if (err.message !== 'Payment cancelled by user') {
        toast.error(err.message || 'Payment failed. Please try again.');
      }
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="mpp-page">
      {/* Header */}
      <div className="mpp-header">
        <div className="mpp-header-content">
          <button className="mpp-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="mpp-title">Complete Payment</h1>
          <p className="mpp-subtitle">Secure payment powered by Razorpay</p>
        </div>
      </div>

      <div className="mpp-container">
        {/* Left: Booking Summary */}
        <div className="mpp-summary">
          <div className="mpp-card">
            <h2 className="mpp-card-title">
              <FaCalendarAlt /> Booking Summary
            </h2>

            <div className="mpp-room-name">{r.name || 'Meeting Room'}</div>

            {(r.city || r.location) && (
              <div className="mpp-info-row">
                <FaMapMarkerAlt className="mpp-icon" />
                <span>{[r.location, r.city, r.state].filter(Boolean).join(', ')}</span>
              </div>
            )}

            <div className="mpp-info-row">
              <FaCalendarAlt className="mpp-icon" />
              <span>{formatDate(startTime)}</span>
            </div>

            <div className="mpp-info-row">
              <FaClock className="mpp-icon" />
              <span>{formatTime(startTime)} – {formatTime(endTime)} ({durationHours.toFixed(1)}h)</span>
            </div>

            {booking.purpose && (
              <div className="mpp-info-row">
                <FaInfoCircle className="mpp-icon" />
                <span>{booking.purpose}</span>
              </div>
            )}

            {booking.attendees && (
              <div className="mpp-info-row">
                <FaUsers className="mpp-icon" />
                <span>{booking.attendees} attendees</span>
              </div>
            )}

            {/* Price Breakdown */}
            <div className="mpp-price-breakdown">
              <div className="mpp-price-row">
                <span>Room ({durationHours.toFixed(1)}h × ₹{pricePerHour.toLocaleString()}/hr)</span>
                <span>₹{baseAmount.toLocaleString()}</span>
              </div>
              {cleaningCharge > 0 && (
                <div className="mpp-price-row">
                  <span>Cleaning charge</span>
                  <span>₹{cleaningCharge.toLocaleString()}</span>
                </div>
              )}
              <div className="mpp-price-divider" />
              <div className="mpp-price-row mpp-price-total">
                <span>Total</span>
                <span>₹{fullAmount.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Security badge */}
          <div className="mpp-security">
            <FaLock className="mpp-security-icon" />
            <div>
              <strong>Secure & Encrypted</strong>
              <p>Your payment is protected by Razorpay's industry-standard security.</p>
            </div>
          </div>
        </div>

        {/* Right: Payment Options */}
        <div className="mpp-payment-panel">
          <div className="mpp-card">
            <h2 className="mpp-card-title">
              <FaCreditCard /> Payment Options
            </h2>

            {/* Payment type selector */}
            <div className="mpp-payment-options">
              <label
                className={`mpp-option ${paymentType === 'full' ? 'selected' : ''}`}
                onClick={() => setPaymentType('full')}
              >
                <div className="mpp-option-radio">
                  <div className={`mpp-radio-dot ${paymentType === 'full' ? 'active' : ''}`} />
                </div>
                <div className="mpp-option-body">
                  <div className="mpp-option-title">
                    Pay Full Amount
                    <span className="mpp-badge recommended">Recommended</span>
                  </div>
                  <div className="mpp-option-amount">₹{fullAmount.toLocaleString()}</div>
                  <div className="mpp-option-desc">Instant confirmation. Pay 100% now.</div>
                </div>
              </label>

              <label
                className={`mpp-option ${paymentType === 'partial' ? 'selected' : ''}`}
                onClick={() => setPaymentType('partial')}
              >
                <div className="mpp-option-radio">
                  <div className={`mpp-radio-dot ${paymentType === 'partial' ? 'active' : ''}`} />
                </div>
                <div className="mpp-option-body">
                  <div className="mpp-option-title">
                    Pay 50% Now
                    <span className="mpp-badge partial">Partial</span>
                  </div>
                  <div className="mpp-option-amount">₹{partialAmount.toLocaleString()}</div>
                  <div className="mpp-option-desc">
                    Pay ₹{partialAmount.toLocaleString()} now. Remaining ₹{partialAmount.toLocaleString()} at the venue.
                  </div>
                </div>
              </label>
            </div>

            {/* Amount to pay now */}
            <div className="mpp-pay-summary">
              <div className="mpp-pay-summary-label">Amount to pay now</div>
              <div className="mpp-pay-summary-amount">₹{payAmount.toLocaleString()}</div>
            </div>

            {/* Pay button */}
            <button
              className="mpp-pay-btn"
              onClick={handlePay}
              disabled={paying || !razorpayReady}
            >
              {paying ? (
                <>
                  <FaSpinner className="mpp-btn-spinner" /> Processing...
                </>
              ) : (
                <>
                  <FaLock /> Pay ₹{payAmount.toLocaleString()} Securely
                </>
              )}
            </button>

            {!razorpayReady && (
              <p className="mpp-loading-note">
                <FaSpinner className="mpp-spinner-inline" /> Loading payment gateway...
              </p>
            )}

            {/* Accepted methods */}
            <div className="mpp-methods">
              <span className="mpp-methods-label">Accepted:</span>
              <span className="mpp-method-badge">📱 UPI</span>
              <span className="mpp-method-badge">💳 Card</span>
              <span className="mpp-method-badge">🏦 Net Banking</span>
              <span className="mpp-method-badge">📷 QR Code</span>
            </div>
          </div>

          {/* Invoice note */}
          <div className="mpp-invoice-note">
            <FaFileInvoice className="mpp-invoice-icon" />
            <p>
              A downloadable invoice will be available in your{' '}
              <button className="mpp-link" onClick={() => navigate('/my-payments')}>
                Payment History
              </button>{' '}
              after successful payment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingPaymentPage;
